// WithdrawForm.tsx
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import { useTranslate } from 'src/locales';
import { useAuthContext } from 'src/auth/hooks';
import FormProvider, { RHFTextField, RHFRadioGroup } from 'src/components/hook-form';

type Props = {
  open: boolean;
  onClose: VoidFunction;
  onCreate: (payload: any) => void;
};

const CHILE_METHODS = ['BANCO_ESTADO', 'FALABELLA', 'TENPO', 'SANTANDER', 'MACH', 'MERCADO_PAGO'];

export default function WithdrawForm({ open, onClose, onCreate }: Props) {
  const { t } = useTranslate();
  const { user } = useAuthContext();

  const WithdrawSchema = Yup.object().shape({
    retraitMethod: Yup.string().required(t('wallet.requiredField')),
    amount: Yup.number().required(t('wallet.requiredField')),
    rut: Yup.string().when('retraitMethod', {
      is: (v: string) => CHILE_METHODS.includes(v),
      then: (sch) => sch.required('RUT requis'),
      otherwise: (sch) => sch.nullable(),
    }),
    accountType: Yup.number().when('retraitMethod', {
      is: (v: string) => CHILE_METHODS.includes(v),
      then: (sch) => sch.required('Type de compte requis').oneOf([1, 2, 3]),
      otherwise: (sch) => sch.nullable(),
    }),
  });

  const defaultValues = {
    retraitMethod: 'OTHER',
    amount: 100,
    phoneNumber: '',
    rut: '',
    accountType: 2 as 1 | 2 | 3,
  };

  const methods = useForm({
    resolver: yupResolver(WithdrawSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = methods;

  const isChile = CHILE_METHODS.includes(watch('retraitMethod'));

  const onSubmit = handleSubmit(async (data) => {
    try {
      onCreate(data);
      reset();
      onClose();
    } catch (error) {
      console.error(error);
    }
  });

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>{t('wallet.withdraw')}</DialogTitle>

        <DialogContent dividers>
          <Stack spacing={3}>
            <RHFRadioGroup
              row
              name="retraitMethod"
              options={[
                { label: 'Moncash', value: 'MONCASH' },
                { label: 'Natcash', value: 'NATCASH' },
                { label: 'Banco Estado', value: 'BANCO_ESTADO' },
                { label: 'Falabella', value: 'FALABELLA' },
                { label: 'Tenpo', value: 'TENPO' },
                { label: 'Santander', value: 'SANTANDER' },
                { label: 'BCI MACH', value: 'MACH' },
                { label: 'Mercado Pago', value: 'MERCADO_PAGO' },
                { label: 'Other', value: 'OTHER' },
              ]}
            />
            {isChile && (
              <>
                <RHFTextField name="rut" label="RUT" />
                <RHFRadioGroup
                  row
                  name="accountType"
                  options={[
                    { label: 'Cuenta Corriente', value: 1 },
                    { label: 'Cuenta Vista', value: 2 },
                    { label: 'Cuenta de Ahorro', value: 3 },
                  ]}
                />
              </>
            )}

            <RHFTextField name="phoneNumber" label={t('deposit_manual_help.account_number')} />

            <RHFTextField
              name="amount"
              label={t('wallet.amount')}
              type="number"
              inputProps={{ min: 1, step: 'any' }}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button color="inherit" variant="outlined" onClick={onClose}>
            {t('wallet.cancel')}
          </Button>

          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            {t('wallet.withdraw')}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
